import { useMutation } from "@apollo/client";
import { intl } from "LocaleWrapper";
import { Alert } from "@mui/material";
import { CharterPdfType, ContactRoleType, SalesCharterStatus } from "__generated__/enums.ts";
import { Button, Checkbox, FormGroup, Input, Textarea } from "@cofano/cck";
import { PdfButton } from "view/charters/dossier/ship/ShipDetailsBlock.tsx";
import { ChangeSalesCharterStatusDocument, SendCharterPdfDocument } from "__generated__/view/charters/graphql/charter.mutations.generated";
import {
	SalesCharterStatusChangeSalesCharterFragment,
} from "__generated__/view/charters/graphql/charter.fragments.generated";
import ApplicationDefinedStatusChanger, {
	ApplicationDefinedDialogProps,
} from "view/statuses/components/changer/ApplicationDefinedStatusChanger";
import SalesCharterStatusSelect from "view/charters/components/selects/SalesCharterStatusSelect";
import {
	SalesCharterStatusChangerInput,
	SalesCharterStatusChangerOutput,
	SalesCharterStatusChangerSchema,
} from "view/charters/utils/charter.schema";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import FormField from "components/cck/FormField";
import SalesCharterEmailRecipientAlert from "view/charters/components/status/SalesCharterEmailRecipientAlert";
import { CustomDialog } from "components/dialogs/Dialogs";
import client from "../../../../apollo";
import { SalesCharterStatusChangesDocument } from "__generated__/view/charters/graphql/charter.queries.generated";

type SalesCharterStatusChangerProps = {
	charter: SalesCharterStatusChangeSalesCharterFragment;
	onCompleted: () => void;
};

const SalesCharterStatusChanger = ({ charter, onCompleted }: SalesCharterStatusChangerProps) => (
	<ApplicationDefinedStatusChanger
		currentStatus={charter.status}
		initStatusChanges={async () => {
			const result = await client.query({
				query: SalesCharterStatusChangesDocument,
				variables: {
					salesCharterId: charter.id,
				},
			});

			return result.data.salesCharterStatusChanges;
		}}
		renderDialog={(dialogProps) => (
			<SalesCharterStatusChangeDialog
				dialogProps={dialogProps}
				charter={charter}
				onCompleted={onCompleted}
			/>
		)}
		renderHistoryItem={(statusChange) => (
			<FormGroup label={intl.formatMessage({ id: "NOTE" })} overflow="hidden">
				<span>{statusChange.notes ?? "-"}</span>
			</FormGroup>
		)}
	/>
);

const SalesCharterStatusChangeDialog = ({
	charter,
	onCompleted,
	dialogProps,
}: ApplicationDefinedDialogProps<SalesCharterStatusChangerProps>) => {
	const [ sendCharterPdf, { loading: sending } ] = useMutation(SendCharterPdfDocument);
	const [ saveCharterStatus, { loading: saving } ] = useMutation(ChangeSalesCharterStatusDocument);

	const loading = saving || sending;

	const { control, handleSubmit, reset, watch, formState: { errors } } = useForm<SalesCharterStatusChangerInput, unknown, SalesCharterStatusChangerOutput>({
		resolver: zodResolver(SalesCharterStatusChangerSchema),
		defaultValues: {
			selectedStatus: null,
			notes: "",
			emailNotes: "",
			includePricing: true,
		} satisfies SalesCharterStatusChangerInput,
	});

	const onStatusChange = async (data: SalesCharterStatusChangerOutput, sendEmail: boolean) => {

		const cancelling = data.selectedStatus.name === SalesCharterStatus.Cancelled;

		if (data.selectedStatus.name === SalesCharterStatus.Confirmed && hasLinkedVoyages) {
			data.selectedStatus.name = SalesCharterStatus.Planned;
		}

		await saveCharterStatus({
			variables: {
				id: charter.id,
				confirmationStatus: data.selectedStatus.name,
				notes: data.notes,
				unlinkVoyage: false,
			},
		});

		if (sendEmail) {
			await sendCharterPdf({
				variables: {
					charterId: charter.id,
					pdfType: cancelling ? CharterPdfType.CancellationToCustomer : CharterPdfType.NominationToCustomer,
					note: data.emailNotes,
					includePricing: data.includePricing,
				},
			});
		}

		onCompleted();
	};

	const [ selectedStatus, includePricing ] = watch([ "selectedStatus", "includePricing" ]);

	const isConfirmation = selectedStatus?.name === SalesCharterStatus.Confirmed;
	const isCancellation = selectedStatus?.name === SalesCharterStatus.Cancelled;

	const hasLinkedVoyages = charter.voyages?.[0]?.ship != null;

	return (
		<CustomDialog
			{...dialogProps}
			onClose={() => {
				dialogProps.onClose();
				reset();
			}}
			actions={isConfirmation ? (
				<>
					<Button
						onClick={handleSubmit(data => onStatusChange(data, false))}
						disabled={loading}
						loading={loading}
						size="small"
						variant="outlined"
					>
						{intl.formatMessage({ id: "CONFIRM" })}
					</Button>

					<Button
						onClick={handleSubmit(data => onStatusChange(data, true))}
						disabled={loading || charter.contacts?.filter(contact =>
							contact.contactRoleType === ContactRoleType.NominationAndConfirmation).length === 0}
						loading={loading}
						size="small"
					>
						{intl.formatMessage({ id: "CONFIRM_AND_SEND" })}
					</Button>
				</>
			) : isCancellation ? (
				<>
					<Button
						onClick={handleSubmit(data => onStatusChange(data, false))}
						disabled={loading}
						loading={loading}
						size="small"
						variant="outlined"
					>
						{intl.formatMessage({ id: "CANCEL_CHARTER" })}
					</Button>

					<Button
						onClick={handleSubmit(data => onStatusChange(data, true))}
						disabled={loading || charter.contacts?.filter(contact =>
							contact.contactRoleType === ContactRoleType.NominationAndConfirmation).length === 0}
						loading={loading}
						size="small"
					>
						{intl.formatMessage({ id: "CANCEL_AND_SEND" })}
					</Button>
				</>
			) : (
				<Button
					onClick={handleSubmit(data => onStatusChange(data, false))}
					loading={loading}
					disabled={loading}
					size="small"
				>
					{intl.formatMessage({ id: "SAVE" })}
				</Button>
			)}
		>
			<form>
				<FormGroup label={intl.formatMessage({ id: "STATUS" })} overflow="hidden" required>
					<FormField errors={errors} name="selectedStatus">
						<Controller
							name="selectedStatus"
							control={control}
							render={({ field }) => (
								<SalesCharterStatusSelect
									{...field}
									currentStatus={charter.status.name}
								/>
							)}
						/>
					</FormField>
				</FormGroup>

				{selectedStatus?.name === SalesCharterStatus.ExecutionCompleted && charter.voyages?.some(x => !x.lastStatusChange?.status.closedStatus && !x.lastStatusChange?.status.finishedStatus) && (
					<Alert severity="warning">
						{intl.formatMessage({ id: "CONFIRM_CHARTER_STATUS_CHANGE_COMPLETE_EXECUTION" })}
					</Alert>
				)}

				{isConfirmation ? (
					<>
						<SalesCharterEmailRecipientAlert
							contacts={charter.contacts ?? []}
							alertBody={intl.formatMessage({ id: "CONFIRM_ORDER_TO_CUSTOMER_CONTENT" })}
						/>

						<FormGroup label={intl.formatMessage({ id: "NOTE" })} overflow="hidden">
							<FormField errors={errors} name="notes">
								<Controller
									name="notes"
									control={control}
									render={({ field }) => (
										<Input {...field} />
									)}
								/>
							</FormField>
						</FormGroup>

						<FormGroup label={intl.formatMessage({ id: "EMAIL_NOTE" })} tooltip={intl.formatMessage({ id: "EMAIL_NOTE_TOOLTIP" })}>
							<FormField errors={errors} name="emailNotes">
								<Controller
									name="emailNotes"
									control={control}
									render={({ field }) => (
										<Textarea {...field} />
									)}
								/>
							</FormField>
						</FormGroup>

						<div style={{ display: "flex", alignItems: "center", flexGrow: 1, justifyContent: "space-between", gap: 8 }}>
							<Controller
								name="includePricing"
								control={control}
								render={({ field }) => (
									<Checkbox
										label={intl.formatMessage({ id: "INCLUDE_PRICING_IN_NOMINATION" })}
										onChange={field.onChange}
										checked={field.value}
									/>
								)}
							/>

							<PdfButton
								charter={charter}
								type={CharterPdfType.NominationToCustomer}
								includePricing={includePricing}
							/>
						</div>
					</>
				) : isCancellation ? (
					<>
						<Alert severity="warning" style={{ marginBottom: 8 }}>
							{intl.formatMessage({ id: "CONFIRM_CHARTER_STATUS_CHANGE_CANCELLED" })}
						</Alert>

						<SalesCharterEmailRecipientAlert
							contacts={charter.contacts ?? []}
							alertBody={intl.formatMessage({ id: "CONFIRM_CANCEL_ORDER_CONTENT" })}
						/>

						<FormGroup label={intl.formatMessage({ id: "NOTE" })} overflow="hidden">
							<FormField errors={errors} name="notes">
								<Controller
									name="notes"
									control={control}
									render={({ field }) => (
										<Input {...field} />
									)}
								/>
							</FormField>
						</FormGroup>

						<FormGroup label={intl.formatMessage({ id: "EMAIL_NOTE" })} tooltip={intl.formatMessage({ id: "EMAIL_NOTE_TOOLTIP" })}>
							<FormField errors={errors} name="emailNotes">
								<Controller
									name="emailNotes"
									control={control}
									render={({ field }) => (
										<Textarea {...field} />
									)}
								/>
							</FormField>
						</FormGroup>
					</>
				) : selectedStatus && (
					<FormGroup label={intl.formatMessage({ id: "NOTE" })} overflow="hidden">
						<FormField errors={errors} name="notes">
							<Controller
								name="notes"
								control={control}
								render={({ field }) => (
									<Input {...field} />
								)}
							/>
						</FormField>
					</FormGroup>
				)}
			</form>
		</CustomDialog>
	);
};

export default SalesCharterStatusChanger;
